import { useCallback, useRef, useState, useEffect } from "react";
import gqlFetch from "lib/gqlFetch";

interface TypingUser {
  userId: string;
  userName: string;
}

const DEBOUNCE_MS = 3000;
const TYPING_TIMEOUT_MS = 4000;

export default function useTypingIndicator(threadId: string | undefined) {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const lastSentRef = useRef<number>(0);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const sendTyping = useCallback(
    (tid?: string) => {
      const targetThread = tid || threadId;
      if (!targetThread) return;
      const now = Date.now();
      if (now - lastSentRef.current < DEBOUNCE_MS) return;
      lastSentRef.current = now;

      gqlFetch(
        `mutation SendTyping($threadId: ID!) { sendTypingIndicator(threadId: $threadId) }`,
        { threadId: targetThread }
      ).catch(() => {});
    },
    [threadId]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  // Method to add a typing user (called from subscription handler externally)
  const addTypingUser = useCallback((user: TypingUser) => {
    setTypingUsers((prev) => {
      const filtered = prev.filter((u) => u.userId !== user.userId);
      return [...filtered, user];
    });
    // Clear after timeout
    const existing = timersRef.current.get(user.userId);
    if (existing) clearTimeout(existing);
    timersRef.current.set(
      user.userId,
      setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => u.userId !== user.userId));
        timersRef.current.delete(user.userId);
      }, TYPING_TIMEOUT_MS)
    );
  }, []);

  // Clear when thread changes
  useEffect(() => {
    setTypingUsers([]);
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
  }, [threadId]);

  return { typingUsers, sendTyping, addTypingUser };
}
